import maplibregl from 'maplibre-gl';
import {
  useId, useEffect, useState, useCallback, useMemo,
} from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { map } from './core/MapView';
import { findFonts } from './core/mapUtil';
import { useMapControlSetting } from './controls/useMapControls';
import { useEffectAsync } from '../reactHelper';
import fetchOrThrow from '../common/util/fetchOrThrow';
import { poisActions } from '../store';

// Mapeamento de ícones Lucide para SVG paths
// Fonte: https://lucide.dev/icons (paths extraídos)
const LUCIDE_PATHS = {
  // Geral
  'map-pin': 'M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z M12 10a2 2 0 1 0 0-4 2 2 0 0 0 0 4Z',
  'map-pinned': 'M18 16.98h-5.99c-1.1 0-1.95.68-2.42 1.7-.41.91-1.02 1.32-1.59 1.32H6.98c-.98 0-1.64-.84-1.32-1.74l2.09-5.86C8.36 10.78 10.23 9 12.54 9h3.91c2.31 0 4.18 1.78 4.8 3.4l2.09 5.86c.32.9-.35 1.74-1.32 1.74h-1.02c-.57 0-1.18-.41-1.59-1.32-.47-1.02-1.32-1.7-2.42-1.7Z M20 10c0 4.993-5.539 10.193-7.399 11.799a1 1 0 0 1-1.202 0C9.539 20.193 4 14.993 4 10a8 8 0 0 1 16 0 M12 10a2 2 0 1 0 0-4 2 2 0 0 0 0 4',
  navigation: 'M3 11l19-9-9 19-2-8-8-2z',
  compass: 'M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10z M16.24 7.76l-2.12 6.36-6.36 2.12 2.12-6.36 6.36-2.12z',
  flag: 'M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z M4 22v-7',
  star: 'M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z',
  heart: 'M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z',
  bookmark: 'M19 21l-7-4-7 4V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2v16z',
  tag: 'M12 2H2v10l9.29 9.29c.94.94 2.48.94 3.42 0l6.58-6.58c.94-.94.94-2.48 0-3.42L12 2Z M7 7h.01',
  circle: 'M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10z',
  // Clientes
  'building-2': 'M6 22V2h12v20 M2 22h20 M6 12h4 M6 7h4 M6 17h4 M14 7h4 M14 12h4 M14 17h4',
  user: 'M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2 M12 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8z',
  users: 'M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2 M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8z M22 21v-2a4 4 0 0 0-3-3.87 M16 3.13a4 4 0 0 1 0 7.75',
  briefcase: 'M16 20V4a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16 M2 10a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2v-8Z',
  store: 'M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z M9 22V12h6v10',
  home: 'M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z M9 22V12h6v10',
  // Bases
  warehouse: 'M22 8.35V20a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V8.35A2 2 0 0 1 3.26 6.5l8-3.2a2 2 0 0 1 1.48 0l8 3.2A2 2 0 0 1 22 8.35Z M6 18h12 M6 14h12 M6 10h12',
  factory: 'M2 20a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V8l-7 5V8l-7 5V4a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2Z M17 18h1 M12 18h1 M7 18h1',
  building: 'M6 22V4a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v18Z M6 12H4a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h2 M18 9h2a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2h-2 M10 6h4 M10 10h4 M10 14h4 M10 18h4',
  landmark: 'M3 22h18 M6 18v-7 M10 18v-7 M14 18v-7 M18 18v-7 M12 2L2 8h20L12 2z',
  castle: 'M8 2v4 M16 2v4 M12 6V2 M4 6h16v4H4z M4 10v12h16V10 M10 22v-6h4v6',
  school: 'M22 10v6M2 10l10-5 10 5-10 5z M6 12v5c3 3 9 3 12 0v-5',
  // Logística
  package: 'M16.5 9.4l-9-5.19 M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z M3.27 6.96L12 12.01l8.73-5.05 M12 22.08V12',
  truck: 'M14 18V6a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v11a1 1 0 0 0 1 1h2 M15 18H9 M19 18h2a1 1 0 0 0 1-1v-3.65a1 1 0 0 0-.22-.624l-3.48-4.35A1 1 0 0 0 17.52 8H14 M17 18a2 2 0 1 0 0-4 2 2 0 0 0 0 4z M7 18a2 2 0 1 0 0-4 2 2 0 0 0 0 4z',
  container: 'M22 7.535V17a3 3 0 0 1-3 3h-1M2 7.535V17a3 3 0 0 0 3 3h1 M7 3h10v4H7V3z M2 7h20 M6 10v7 M10 10v7 M14 10v7 M18 10v7',
  boxes: 'M2.97 12.92A2 2 0 0 0 2 14.63v3.24a2 2 0 0 0 .97 1.71l3 1.8a2 2 0 0 0 2.06 0L12 19v-5.5l-5-3-4.03 2.42Z M7 16.5l-4.74-2.85 M7 16.5l5-3 M7 16.5v5.17 M12 13.5V19l3.97 2.38a2 2 0 0 0 2.06 0l3-1.8a2 2 0 0 0 .97-1.71v-3.24a2 2 0 0 0-.97-1.71L17 10.5l-5 3Z M17 16.5l-5-3 M17 16.5l4.74-2.85 M17 16.5v5.17 M7.97 4.42A2 2 0 0 0 7 6.13v4.37l5 3 5-3V6.13a2 2 0 0 0-.97-1.71l-3-1.8a2 2 0 0 0-2.06 0l-3 1.8Z M12 8l4.74-2.85 M12 8L7.26 5.15 M12 13.5V8',
  'package-check': 'M16.5 9.4l-9-5.19 M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l.5-.28 M3.27 6.96L12 12.01l8.73-5.05 M12 22.08V12 M16 18l2 2 4-4',
  'package-x': 'M21 10V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l2-1.14 M16.5 9.4l-9-5.19 M3.27 6.96L12 12.01l8.73-5.05 M12 22.08V12 M17 13l5 5m-5 0l5-5',
  forklift: 'M5 11V2a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1v4l3-1v6H5z M2 21v-3a2 2 0 0 1 2-2h8 M22 10h-6V6h6v4z M5 21a2 2 0 1 0 0-4 2 2 0 0 0 0 4z M17 21a2 2 0 1 0 0-4 2 2 0 0 0 0 4z M7 17H15',
  // Automotivo
  car: 'M19 17h2c.6 0 1-.4 1-1v-3c0-.9-.7-1.7-1.5-1.9C18.7 10.6 16 10 16 10s-1.3-1.4-2.2-2.3c-.5-.4-1.1-.7-1.8-.7H5c-.6 0-1.1.4-1.4.9l-1.4 2.9A3.7 3.7 0 0 0 2 12v4c0 .6.4 1 1 1h2 M7 17a2 2 0 1 0 0-4 2 2 0 0 0 0 4z M17 17a2 2 0 1 0 0-4 2 2 0 0 0 0 4z',
  'car-front': 'M21 8l-2 2-1.5-3.7A2 2 0 0 0 15.646 5H8.4a2 2 0 0 0-1.903 1.257L5 10 3 8 M7 14h.01 M17 14h.01 M4 10h16v8a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2v-8z',
  bike: 'M5 19a4 4 0 1 0 0-8 4 4 0 0 0 0 8z M19 19a4 4 0 1 0 0-8 4 4 0 0 0 0 8z M12 19V5l3 3-3 3',
  bus: 'M8 6v6 M16 6v6 M2 12h19.6 M18 18h3s.5-1.7.8-2.8c.1-.4.2-.8.2-1.2 0-.4-.1-.8-.2-1.2l-1.4-5C20.1 6.8 19.1 6 18 6H4a2 2 0 0 0-2 2v10h3 M7 18h10 M9 18a2 2 0 1 0-4 0 M20 18a2 2 0 1 0-4 0',
  ambulance: 'M10 10H6 M10 14V6 M11 6h4.3a2 2 0 0 1 1.58.78l3.62 4.68c.3.39.5.88.5 1.38V17a1 1 0 0 1-1 1h-2 M7 18a2 2 0 1 0 0-4 2 2 0 0 0 0 4z M17 18a2 2 0 1 0 0-4 2 2 0 0 0 0 4z M11 18H9 M5 18H2a1 1 0 0 1-1-1V8a1 1 0 0 1 1-1h4.5a1 1 0 0 0 .82-.44l1.1-1.6A1 1 0 0 1 9.24 5H14',
  fuel: 'M3 22V5a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v17 M13 10h4a2 2 0 0 1 2 2v2a2 2 0 0 0 2 2 M2 22h14 M13 13V7 M6 13v2',
  'parking-circle': 'M12 22c5.52 0 10-4.48 10-10S17.52 2 12 2 2 6.48 2 12s4.48 10 10 10z M9 17V7h4a3 3 0 0 1 0 6H9',
  wrench: 'M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z',
  'circle-gauge': 'M12 22c5.52 0 10-4.48 10-10S17.52 2 12 2 2 6.48 2 12s4.48 10 10 10z M12 6v2 M16.24 7.76l-1.41 1.41 M18 12h-2 M16.24 16.24l-1.41-1.41 M12 18v-2 M7.76 16.24l1.41-1.41 M6 12h2 M7.76 7.76l1.41 1.41 M12 14a2 2 0 1 0 0-4 2 2 0 0 0 0 4z',
  // Transporte
  ship: 'M2 21c.6.5 1.2 1 2.5 1 2.5 0 2.5-2 5-2 1.3 0 1.9.5 2.5 1 .6.5 1.2 1 2.5 1 2.5 0 2.5-2 5-2 1.3 0 1.9.5 2.5 1 M19.38 20A11.6 11.6 0 0 0 21 14l-9-4-9 4c0 2.9.94 5.34 2.81 7.76 M19 13V7a2 2 0 0 0-2-2H7a2 2 0 0 0-2 2v6 M12 10v4 M12 2v3',
  plane: 'M17.8 19.2L16 11l3.5-3.5C21 6 21.5 4 21 3c-1-.5-3 0-4.5 1.5L13 8 4.8 6.2c-.5-.1-.9.1-1.1.5l-.3.5c-.2.5-.1 1 .3 1.3L9 12l-2 3H4l-1 1 3 2 2 3 1-1v-3l3-2 3.5 5.3c.3.4.8.5 1.3.3l.5-.2c.4-.3.6-.7.5-1.2z',
  train: 'M4 15.5a3.5 3.5 0 1 0 7 0 3.5 3.5 0 1 0-7 0 M17.5 4.5l-4 4 M3 4h6l7 7v11 M17 17l4 4 M21 11V5a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v6h18z M13 15.5a3.5 3.5 0 1 0 7 0 3.5 3.5 0 1 0-7 0',
  tractor: 'M3 4h9l1 7 M4 11V4 M8 10V4 M18 5l.5 1 M3 16a3 3 0 1 0 6 0 3 3 0 1 0-6 0 M16 16a3.5 3.5 0 1 0 7 0 3.5 3.5 0 1 0-7 0 M14 12l-2 5h8l-2-5 M14 12h5l-1-7h-3',
  sailboat: 'M22 18H2a4 4 0 0 0 4 4h12a4 4 0 0 0 4-4Z M2 18l9.5-9.5L15 12 M12 2v6.5',
  cable: 'M4 9a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2a2 2 0 0 1-2 2 M3 15h2 M7 15h6 M16 9a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2a2 2 0 0 1-2 2 M14 15h1 M19 15h2 M4 9v6 M18 9v6',
  'tram-front': 'M4 6l2-4h12l2 4 M4 6h16v8a4 4 0 0 1-4 4H8a4 4 0 0 1-4-4V6z M8 6v4 M16 6v4 M9.5 22l1-4h3l1 4 M8 22h8',
  // Segurança
  shield: 'M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z',
  'shield-alert': 'M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z M12 8v4 M12 16h.01',
  'shield-check': 'M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z M9 12l2 2 4-4',
  camera: 'M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z M12 17a4 4 0 1 0 0-8 4 4 0 0 0 0 8z',
  siren: 'M7 12a5 5 0 0 1 5-5v0a5 5 0 0 1 5 5v6H7v-6Z M5 20a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2v1a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1v-1Z M8 11V8 M16 11V8 M12 11V4',
  lock: 'M19 11H5a2 2 0 0 0-2 2v7a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7a2 2 0 0 0-2-2Z M7 11V7a5 5 0 0 1 10 0v4',
  eye: 'M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6z',
  bell: 'M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9 M13.73 21a2 2 0 0 1-3.46 0',
  // Paradas
  coffee: 'M18 8h1a4 4 0 0 1 0 8h-1 M2 8h16v9a4 4 0 0 1-4 4H6a4 4 0 0 1-4-4V8z M6 1v3 M10 1v3 M14 1v3',
  'utensils-crossed': 'M12 12l-8 8 M12 8l-8 8 M2.5 2.5l19 19 M20 3.5v7.5c0 .46-.05.9-.14 1.32a6.97 6.97 0 0 1-.86 2.18 M4 3.5v7.5c0 .46.05.9.14 1.32.18.67.47 1.3.86 1.86',
  bed: 'M3 20v-8a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v8 M5 10V6a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2v4 M3 18h18',
  bath: 'M9 6l1.5 1.5a3.18 3.18 0 0 1 0 4.5l-.88.88a3.18 3.18 0 0 0 0 4.5l1.13 1.12 M2 12h20 M3 12v7a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7 M3 12V5a2 2 0 0 1 2-2h1',
  wifi: 'M5 12.55a11 11 0 0 1 14.08 0 M1.42 9a16 16 0 0 1 21.16 0 M8.53 16.11a6 6 0 0 1 6.95 0 M12 20h.01',
  tv: 'M2 7a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V7z M10 21l2-2 2 2',
  sofa: 'M20 9V6a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v3 M2 11v5a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-5a2 2 0 0 0-4 0v2H6v-2a2 2 0 0 0-4 0Z M4 18v2 M20 18v2 M12 4v9',
  armchair: 'M19 9V6a2 2 0 0 0-2-2H7a2 2 0 0 0-2 2v3 M3 11v5a3 3 0 0 0 3 3h12a3 3 0 0 0 3-3v-5a2 2 0 0 0-4 0v2H7v-2a2 2 0 0 0-4 0Z M5 18v2 M19 18v2',
  // Serviços
  stethoscope: 'M4.8 2.3A.3.3 0 1 0 5 2H4a2 2 0 0 0-2 2v5a6 6 0 0 0 6 6v0a6 6 0 0 0 6-6V4a2 2 0 0 0-2-2h-1a.2.2 0 1 0 .3.3 M8 15v1a6 6 0 0 0 6 6v0a6 6 0 0 0 6-6v-4 M22 10a2 2 0 1 0-4 0 2 2 0 0 0 4 0z',
  'graduation-cap': 'M22 10v6M2 10l10-5 10 5-10 5z M6 12v5c3 3 9 3 12 0v-5',
  gavel: 'M14.5 6.5l-5 5L2.17 4.17a2.12 2.12 0 0 1 3-3l7.33 7.33z M8.5 9.5l-4 4 6 6 4-4z M14.5 6.5L17 4l3 3-2.5 2.5z M22 22L12 12',
  banknote: 'M2 8a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V8z M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6z M6 12h.01 M18 12h.01',
  'shopping-cart': 'M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6 M9 22a1 1 0 1 0 0-2 1 1 0 0 0 0 2z M20 22a1 1 0 1 0 0-2 1 1 0 0 0 0 2z',
  scissors: 'M6 9a3 3 0 1 0 0-6 3 3 0 0 0 0 6z M6 21a3 3 0 1 0 0-6 3 3 0 0 0 0 6z M20 4L8.12 15.88 M14.47 14.48L20 20 M8.12 8.12L12 12',
  brush: 'M9.06 11.9l8.07-8.06a2.85 2.85 0 1 1 4.03 4.03l-8.06 8.08 M7.07 14.94c-1.66 0-3 1.35-3 3.02 0 1.33-2.5 1.52-2 2.02 1.08 1.1 2.49 2.02 4 2.02 2.2 0 4-1.8 4-4.04a3.01 3.01 0 0 0-3-3.02z',
  // Comércio
  'shopping-bag': 'M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z M3 6h18 M16 10a4 4 0 0 1-8 0',
  receipt: 'M4 2v20l2-1 2 1 2-1 2 1 2-1 2 1 2-1 2 1V2l-2 1-2-1-2 1-2-1-2 1-2-1-2 1-2-1z M16 8h-6a2 2 0 1 0 0 4h4a2 2 0 1 1 0 4H8 M12 17V7',
  'credit-card': 'M1 10h22 M1 6a2 2 0 0 1 2-2h18a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V6z',
  wallet: 'M21 12V7H5a2 2 0 0 1 0-4h14v4 M3 5v14a2 2 0 0 0 2 2h16v-5 M18 12a2 2 0 0 0 0 4h4v-4h-4z',
  'piggy-bank': 'M19 5c-1.5 0-2.8 1.4-3 2-3.5-1.5-11-.3-11 5 0 1.8 0 3 2 4.5V20h4v-2h3v2h4v-4c1-.5 1.7-1 2-2h2v-4h-2c0-1-.5-1.5-1-2V5z M2 9v1c0 1.1.9 2 2 2h1',
  'trending-up': 'M23 6l-9.5 9.5-5-5L1 18 M17 6h6v6',
  // Agro
  wheat: 'M2 22L16 8 M3.47 12.53L5 11l1.53 1.53a3.5 3.5 0 0 1 0 4.94L5 19l-1.53-1.53a3.5 3.5 0 0 1 0-4.94Z M7.47 8.53L9 7l1.53 1.53a3.5 3.5 0 0 1 0 4.94L9 15l-1.53-1.53a3.5 3.5 0 0 1 0-4.94Z M11.47 4.53L13 3l1.53 1.53a3.5 3.5 0 0 1 0 4.94L13 11l-1.53-1.53a3.5 3.5 0 0 1 0-4.94Z M20 2h2v2a4 4 0 0 1-4 4h-2V6a4 4 0 0 1 4-4Z M11.47 17.47L13 19l-1.53 1.53a3.5 3.5 0 0 1-4.94 0L5 19l1.53-1.53a3.5 3.5 0 0 1 4.94 0Z M15.47 13.47L17 15l-1.53 1.53a3.5 3.5 0 0 1-4.94 0L9 15l1.53-1.53a3.5 3.5 0 0 1 4.94 0Z M19.47 9.47L21 11l-1.53 1.53a3.5 3.5 0 0 1-4.94 0L13 11l1.53-1.53a3.5 3.5 0 0 1 4.94 0Z',
  leaf: 'M11 20A7 7 0 0 1 9.8 6.1C15.5 5 17 4.48 19 2c1 2 2 4.18 2 8 0 5.5-4.78 10-10 10Z M2 21c0-3 1.85-5.36 5.08-6C9.5 14.52 12 13 13 12',
  trees: 'M10 10v.2A3 3 0 0 1 8.9 16v0H5v0h0a3 3 0 0 1-1-5.8V10a3 3 0 0 1 6 0Z M7 16v6 M13.9 7.9A3 3 0 0 1 19 10v.2a3 3 0 0 1-1 5.8h-3.9v0h0a3 3 0 0 1-1-5.8V10a3 3 0 0 1 .9-2.1Z M15 16v6',
  'flower-2': 'M12 5a3 3 0 1 1 3 3m-3-3a3 3 0 1 0-3 3m3-3v1M9 8a3 3 0 1 0 3 3M9 8h1m5 0a3 3 0 1 1-3 3m3-3h-1m-2 3v-1 M12 8a8 8 0 0 0 2 4.5A8 8 0 0 0 16 16a8 8 0 0 0-8 0 8 8 0 0 0 2-3.5A8 8 0 0 0 12 8Z M12 14v8 M12 22c4-4 8 0 8 0s-4 0-8 0-8-4-8 0 4 0 8 0',
  bug: 'M8 2l1.88 1.88 M14.12 3.88L16 2 M9 7.13v-1a3.003 3.003 0 1 1 6 0v1 M12 20c-3.3 0-6-2.7-6-6v-3a4 4 0 0 1 4-4h4a4 4 0 0 1 4 4v3c0 3.3-2.7 6-6 6 M12 20v-9 M6.53 9C4.6 8.8 3 7.1 3 5 M6 13H2 M3 21c0-2.1 1.7-3.9 3.8-4 M20.97 5c0 2.1-1.6 3.8-3.5 4 M22 13h-4 M17.2 17c2.1.1 3.8 1.9 3.8 4',
  sun: 'M12 17a5 5 0 1 0 0-10 5 5 0 0 0 0 10z M12 1v2 M12 21v2 M4.22 4.22l1.42 1.42 M18.36 18.36l1.42 1.42 M1 12h2 M21 12h2 M4.22 19.78l1.42-1.42 M18.36 5.64l1.42-1.42',
  // Construção
  'hard-hat': 'M2 18a1 1 0 0 0 1 1h18a1 1 0 0 0 1-1v-2a1 1 0 0 0-1-1H3a1 1 0 0 0-1 1v2z M10 10V5a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1v5 M4 15v-3a8 8 0 0 1 16 0v3 M12 10V5',
  hammer: 'M15 12l-8.5 8.5c-.83.83-2.17.83-3 0 0 0 0 0 0 0a2.12 2.12 0 0 1 0-3L12 9 M17.64 15L22 10.64 M20.91 11.7l-1.25-1.25c-.6-.6-.93-1.4-.93-2.25v-.86L16.01 4.6a5.56 5.56 0 0 0-3.94-1.64H9l.92.82A6.18 6.18 0 0 1 12 8.4v1.56l2 2h2.47l2.26 1.91',
  ruler: 'M21.5 10.3l-9.4-9.4c-.6-.6-1.6-.6-2.1 0L2.3 8.6c-.6.6-.6 1.5 0 2.1l9.4 9.4c.6.6 1.5.6 2.1 0l7.7-7.7c.6-.6.6-1.6 0-2.1z M14.5 6.5l1 1 M10.5 10.5l1 1 M6.5 14.5l1 1',
  'paint-bucket': 'M19 11h.01 M17.5 7.5c0-1.5-.5-3-2-4 M21 21l-6-6 M9 3v6l-3 3v6a3 3 0 0 0 3 3h4a3 3 0 0 0 3-3v-6l-3-3V3a2 2 0 0 0-2-2H7a2 2 0 0 0-2 2v2h6',
  cone: 'M9.77 4.23l-4.54 4.54a2 2 0 0 0 0 2.83l7.17 7.17a2 2 0 0 0 2.83 0l4.54-4.54a2 2 0 0 0 0-2.83l-7.17-7.17a2 2 0 0 0-2.83 0Z M15.1 8.9L8.5 15.5',
};

/**
 * Gera SVG de marcador com cor e ícone Lucide
 * Design premium: pin colorido com ícone branco, sem círculo interno
 * Tamanho otimizado (44x54) - balanceado entre visibilidade e densidade
 */
const generateLucideMarkerSvg = (iconCode, color) => {
  const path = LUCIDE_PATHS[iconCode] || LUCIDE_PATHS['map-pin'];

  return `
    <svg xmlns="http://www.w3.org/2000/svg" width="44" height="54" viewBox="0 0 44 54">
      <defs>
        <!-- Sombra suave -->
        <filter id="shadow-${color.replace('#', '')}" x="-30%" y="-30%" width="160%" height="160%">
          <feDropShadow dx="0" dy="1" stdDeviation="2" flood-color="rgba(0,0,0,0.35)"/>
        </filter>
      </defs>
      <!-- Sombra no chão -->
      <ellipse cx="22" cy="51" rx="8" ry="3" fill="rgba(0,0,0,0.25)"/>
      <!-- Pin shape com sombra -->
      <path d="M22 3 C10.95 3 2 11.95 2 23 C2 38 22 51 22 51 C22 51 42 38 42 23 C42 11.95 33.05 3 22 3 Z"
            fill="${color}"
            stroke="rgba(255,255,255,0.4)"
            stroke-width="1.5"
            filter="url(#shadow-${color.replace('#', '')})"/>
      <!-- Ícone branco centralizado -->
      <g transform="translate(10, 10) scale(1)" fill="none" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <path d="${path}"/>
      </g>
    </svg>
  `;
};

/**
 * Gera SVG de marcador padrão com cor (sem ícone específico)
 * Design premium: pin colorido com círculo branco pequeno no centro
 * Tamanho otimizado (44x54)
 */
const generateDefaultMarkerSvg = (color) => `
    <svg xmlns="http://www.w3.org/2000/svg" width="44" height="54" viewBox="0 0 44 54">
      <defs>
        <filter id="shadowDef-${color.replace('#', '')}" x="-30%" y="-30%" width="160%" height="160%">
          <feDropShadow dx="0" dy="1" stdDeviation="2" flood-color="rgba(0,0,0,0.35)"/>
        </filter>
      </defs>
      <!-- Sombra no chão -->
      <ellipse cx="22" cy="51" rx="8" ry="3" fill="rgba(0,0,0,0.25)"/>
      <!-- Pin shape -->
      <path d="M22 3 C10.95 3 2 11.95 2 23 C2 38 22 51 22 51 C22 51 42 38 42 23 C42 11.95 33.05 3 22 3 Z"
            fill="${color}"
            stroke="rgba(255,255,255,0.4)"
            stroke-width="1.5"
            filter="url(#shadowDef-${color.replace('#', '')})"/>
      <!-- Círculo branco central pequeno -->
      <circle cx="22" cy="21" r="6" fill="white" opacity="0.9"/>
    </svg>
  `;

/**
 * Converte SVG string para data URL
 */
const svgToDataUrl = (svg) => `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg.trim())}`;

/**
 * Gera marcador com ícone PNG dentro do pin colorido
 * Retorna uma Promise com o data URL da imagem combinada
 * Tamanho otimizado (44x54) para balancear visibilidade e densidade
 */
const generatePngMarker = (pngPath, color) => new Promise((resolve) => {
  const canvas = document.createElement('canvas');
  canvas.width = 44;
  canvas.height = 54;
  const ctx = canvas.getContext('2d');

  // Sombra no chão
  ctx.beginPath();
  ctx.ellipse(22, 51, 8, 3, 0, 0, 2 * Math.PI);
  ctx.fillStyle = 'rgba(0, 0, 0, 0.25)';
  ctx.fill();

  // Sombra drop shadow manual
  ctx.shadowColor = 'rgba(0, 0, 0, 0.35)';
  ctx.shadowBlur = 4;
  ctx.shadowOffsetX = 0;
  ctx.shadowOffsetY = 1;

  // Desenhar o pin shape
  ctx.beginPath();
  ctx.moveTo(22, 3);
  ctx.bezierCurveTo(10.95, 3, 2, 11.95, 2, 23);
  ctx.bezierCurveTo(2, 38, 22, 51, 22, 51);
  ctx.bezierCurveTo(22, 51, 42, 38, 42, 23);
  ctx.bezierCurveTo(42, 11.95, 33.05, 3, 22, 3);
  ctx.closePath();

  ctx.fillStyle = color;
  ctx.fill();

  // Resetar shadow antes da borda
  ctx.shadowColor = 'transparent';
  ctx.shadowBlur = 0;

  // Borda clara
  ctx.strokeStyle = 'rgba(255, 255, 255, 0.4)';
  ctx.lineWidth = 1.5;
  ctx.stroke();

  // Carregar e desenhar o ícone PNG
  const img = new Image();
  img.crossOrigin = 'anonymous';
  img.onload = () => {
    // Desenhar o PNG centralizado
    const iconSize = 22;
    const iconX = 22 - iconSize / 2;
    const iconY = 20 - iconSize / 2;

    ctx.globalCompositeOperation = 'source-over';
    ctx.drawImage(img, iconX, iconY, iconSize, iconSize);
    resolve(canvas.toDataURL('image/png'));
  };
  img.onerror = () => {
    // Fallback: desenhar círculo branco se PNG falhar
    ctx.beginPath();
    ctx.arc(22, 21, 6, 0, 2 * Math.PI);
    ctx.fillStyle = 'white';
    ctx.globalAlpha = 0.9;
    ctx.fill();
    resolve(canvas.toDataURL('image/png'));
  };
  // Construir URL completa para o ícone PNG
  img.src = pngPath.startsWith('/') ? pngPath : `/images/${pngPath}`;
});

const MapPoi = ({ mapReady }) => {
  const id = useId();
  const dispatch = useDispatch();

  const pois = useSelector((state) => state.pois.items);
  const poiIcons = useSelector((state) => state.pois.icons);
  const groupFilters = useSelector((state) => state.pois.groupFilters);

  const [loadedMarkers, setLoadedMarkers] = useState(new Set());
  const [markersReady, setMarkersReady] = useState(false);

  // Map controls panel settings - usando hook individual para melhor performance
  const showPoi = useMapControlSetting('showPoi', true);

  // Criar mapa de ícones por ID para lookup rápido (memoizado para evitar re-renders)
  const iconsById = useMemo(() => poiIcons.reduce((acc, icon) => {
    acc[icon.id] = icon;
    return acc;
  }, {}), [poiIcons]);

  // Load POIs and icons on mount
  useEffectAsync(async () => {
    try {
      const [poisRes, iconsRes] = await Promise.all([
        fetchOrThrow('/api/pois'),
        fetchOrThrow('/api/poiicons'),
      ]);
      const poisData = await poisRes.json();
      const iconsData = await iconsRes.json();

      dispatch(poisActions.refresh(poisData));
      dispatch(poisActions.setIcons(iconsData));
    } catch (error) {
      console.error('Error loading POIs:', error);
    }
  }, []);

  // Load image from data URL
  const loadImageFromDataUrl = useCallback((imageId, dataUrl) => new Promise((resolve) => {
    if (map.hasImage(imageId)) {
      resolve(imageId);
      return;
    }

    const img = new Image();
    img.onload = () => {
      if (map && !map.hasImage(imageId)) {
        map.addImage(imageId, img);
      }
      resolve(imageId);
    };
    img.onerror = () => {
      console.warn(`Failed to load marker: ${imageId}`);
      resolve(null);
    };
    img.src = dataUrl;
  }), []);

  // Generate and load markers for all POIs
  useEffectAsync(async () => {
    if (!mapReady || !map || Object.keys(pois).length === 0) return;

    const poisArray = Object.values(pois);
    const loadPromises = [];
    const markerIds = new Set();

    // Load default marker
    const defaultMarkerId = 'poi-marker-default';
    if (!map.hasImage(defaultMarkerId)) {
      const defaultSvg = generateDefaultMarkerSvg('#3B82F6');
      loadPromises.push(loadImageFromDataUrl(defaultMarkerId, svgToDataUrl(defaultSvg)));
    }
    markerIds.add(defaultMarkerId);

    // Generate unique marker for each POI based on color + icon
    for (const poi of poisArray) {
      const color = poi.color || '#3B82F6';
      const iconCode = poi.iconCode || null;
      const iconType = poi.iconType || null;
      const iconId = poi.iconId || null;

      // Create unique marker ID based on type, color and icon
      let markerId;

      if (iconType === 'lucide' && iconCode) {
        // Ícone Lucide (SVG gerado dinamicamente)
        markerId = `poi-marker-lucide-${iconCode}-${color.replace('#', '')}`;
        if (!map.hasImage(markerId) && !markerIds.has(markerId)) {
          const svg = generateLucideMarkerSvg(iconCode, color);
          loadPromises.push(loadImageFromDataUrl(markerId, svgToDataUrl(svg)));
          markerIds.add(markerId);
        }
      } else if (iconType === 'png' && iconId && iconsById[iconId]?.path) {
        // Ícone PNG do banco de dados
        const iconPath = iconsById[iconId].path;
        markerId = `poi-marker-png-${iconId}-${color.replace('#', '')}`;
        if (!map.hasImage(markerId) && !markerIds.has(markerId)) {
          // Gerar marcador com PNG
          loadPromises.push(
            generatePngMarker(iconPath, color).then((dataUrl) =>
              loadImageFromDataUrl(markerId, dataUrl)),
          );
          markerIds.add(markerId);
        }
      } else if (iconId && iconsById[iconId]?.path) {
        // Fallback: iconId existe mas sem iconType definido (ícones legados)
        const iconPath = iconsById[iconId].path;
        markerId = `poi-marker-png-${iconId}-${color.replace('#', '')}`;
        if (!map.hasImage(markerId) && !markerIds.has(markerId)) {
          loadPromises.push(
            generatePngMarker(iconPath, color).then((dataUrl) =>
              loadImageFromDataUrl(markerId, dataUrl)),
          );
          markerIds.add(markerId);
        }
      } else {
        // Sem ícone definido: usar marcador de cor padrão
        markerId = `poi-marker-color-${color.replace('#', '')}`;
        if (!map.hasImage(markerId) && !markerIds.has(markerId)) {
          const svg = generateDefaultMarkerSvg(color);
          loadPromises.push(loadImageFromDataUrl(markerId, svgToDataUrl(svg)));
          markerIds.add(markerId);
        }
      }
    }

    const results = await Promise.all(loadPromises);
    const loaded = new Set(results.filter(Boolean));
    loaded.add(defaultMarkerId);
    setLoadedMarkers((prev) => new Set([...prev, ...loaded]));
    setMarkersReady(true);
  }, [mapReady, pois, poiIcons, loadImageFromDataUrl, iconsById]);

  // Create source and layers
  useEffect(() => {
    if (!mapReady || !map) return;

    if (!map.getSource(id)) {
      map.addSource(id, {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: [] },
        cluster: true,
        clusterMaxZoom: 14, // A partir do zoom 15, mostra POIs individuais
        clusterRadius: 50, // Raio em pixels para agrupar
      });
    }

    // Layer para clusters
    if (!map.getLayer(`${id}-clusters`)) {
      map.addLayer({
        id: `${id}-clusters`,
        type: 'circle',
        source: id,
        filter: ['has', 'point_count'],
        paint: {
          'circle-color': [
            'step',
            ['get', 'point_count'],
            '#3B82F6', // azul para poucos
            10,
            '#F59E0B', // laranja para médio
            50,
            '#EF4444', // vermelho para muitos
          ],
          'circle-radius': [
            'step',
            ['get', 'point_count'],
            18,
            10,
            24,
            50,
            30,
          ],
          'circle-stroke-width': 2,
          'circle-stroke-color': '#ffffff',
        },
      });
    }

    // Layer para contagem dos clusters
    if (!map.getLayer(`${id}-cluster-count`)) {
      map.addLayer({
        id: `${id}-cluster-count`,
        type: 'symbol',
        source: id,
        filter: ['has', 'point_count'],
        layout: {
          'text-field': '{point_count_abbreviated}',
          'text-font': findFonts(map),
          'text-size': 12,
        },
        paint: {
          'text-color': '#ffffff',
        },
      });
    }

    if (!map.getLayer(`${id}-icons`)) {
      map.addLayer({
        id: `${id}-icons`,
        source: id,
        type: 'symbol',
        filter: ['!', ['has', 'point_count']], // Apenas POIs não clusterizados
        layout: {
          'icon-image': ['get', 'marker'],
          // Escala dinâmica baseada no zoom - menor quando afastado, maior quando próximo
          'icon-size': [
            'interpolate', ['linear'], ['zoom'],
            4, 0.35,  // Zoom continental - 35% do tamanho
            8, 0.5,   // Zoom regional - 50%
            12, 0.75, // Zoom cidade - 75%
            15, 1.0,  // Zoom rua - 100% (tamanho normal)
            18, 1.1,  // Zoom muito próximo - 110%
          ],
          'icon-allow-overlap': true,
          'icon-anchor': 'bottom',
        },
      });
    }

    if (!map.getLayer(`${id}-labels`)) {
      map.addLayer({
        id: `${id}-labels`,
        source: id,
        type: 'symbol',
        filter: ['!', ['has', 'point_count']], // Apenas POIs não clusterizados
        layout: {
          'text-field': ['get', 'name'],
          'text-anchor': 'top',
          'text-offset': [0, 0.3],
          'text-font': findFonts(map),
          // Texto também escala com zoom
          'text-size': [
            'interpolate', ['linear'], ['zoom'],
            4, 8,    // Zoom continental - fonte 8px
            10, 10,  // Zoom regional - fonte 10px
            14, 12,  // Zoom cidade - fonte 12px
            18, 14,  // Zoom rua - fonte 14px
          ],
          'text-allow-overlap': false,
        },
        paint: {
          'text-color': '#333333',
          'text-halo-color': 'white',
          'text-halo-width': 1.5,
        },
      });
    }

    // Click handler para clusters - zoom in
    const onClusterClick = (e) => {
      const features = map.queryRenderedFeatures(e.point, { layers: [`${id}-clusters`] });
      if (!features.length) return;
      const clusterId = features[0].properties.cluster_id;
      map.getSource(id).getClusterExpansionZoom(clusterId, (err, zoom) => {
        if (err) return;
        map.easeTo({
          center: features[0].geometry.coordinates,
          zoom: zoom + 1,
        });
      });
    };

    // Enhanced click handler with more POI info
    const onPoiClick = (e) => {
      const feature = e.features[0];
      if (feature) {
        const props = feature.properties;
        const coordinates = feature.geometry.coordinates.slice();

        // Build popup content with available info
        let content = `
          <div style="padding: 12px; min-width: 200px; max-width: 300px;">
            <div style="display: flex; align-items: center; gap: 8px; margin-bottom: 8px;">
              <strong style="font-size: 15px; color: ${props.color || '#333'};">${props.name}</strong>
              ${props.favorite === 'true' ? '<span style="color: #F59E0B;">★</span>' : ''}
            </div>
        `;

        if (props.description) {
          content += `<p style="margin: 0 0 8px 0; font-size: 13px; color: #666;">${props.description}</p>`;
        }

        if (props.address || props.city) {
          content += '<div style="font-size: 12px; color: #888; margin-bottom: 4px;">';
          if (props.address) content += `📍 ${props.address}<br/>`;
          if (props.city) {
            content += `${props.city}`;
            if (props.state) content += `, ${props.state}`;
            if (props.postalCode) content += ` - ${props.postalCode}`;
          }
          content += '</div>';
        }

        if (props.contactName || props.contactPhone || props.contactEmail) {
          content += '<div style="font-size: 12px; color: #888; border-top: 1px solid #eee; margin-top: 8px; padding-top: 8px;">';
          if (props.contactName) content += `👤 ${props.contactName}<br/>`;
          if (props.contactPhone) content += `📞 ${props.contactPhone}<br/>`;
          if (props.contactEmail) content += `✉️ ${props.contactEmail}`;
          content += '</div>';
        }

        content += '</div>';

        new maplibregl.Popup({ closeButton: true, closeOnClick: true, maxWidth: '320px' })
          .setLngLat(coordinates)
          .setHTML(content)
          .addTo(map);
      }
    };

    const onMouseEnter = () => {
      map.getCanvas().style.cursor = 'pointer';
    };

    const onMouseLeave = () => {
      map.getCanvas().style.cursor = '';
    };

    map.on('click', `${id}-icons`, onPoiClick);
    map.on('click', `${id}-clusters`, onClusterClick);
    map.on('mouseenter', `${id}-icons`, onMouseEnter);
    map.on('mouseenter', `${id}-clusters`, onMouseEnter);
    map.on('mouseleave', `${id}-icons`, onMouseLeave);
    map.on('mouseleave', `${id}-clusters`, onMouseLeave);

    return () => {
      map.off('click', `${id}-icons`, onPoiClick);
      map.off('click', `${id}-clusters`, onClusterClick);
      map.off('mouseenter', `${id}-icons`, onMouseEnter);
      map.off('mouseenter', `${id}-clusters`, onMouseEnter);
      map.off('mouseleave', `${id}-icons`, onMouseLeave);
      map.off('mouseleave', `${id}-clusters`, onMouseLeave);

      if (map.getLayer(`${id}-icons`)) map.removeLayer(`${id}-icons`);
      if (map.getLayer(`${id}-labels`)) map.removeLayer(`${id}-labels`);
      if (map.getLayer(`${id}-clusters`)) map.removeLayer(`${id}-clusters`);
      if (map.getLayer(`${id}-cluster-count`)) map.removeLayer(`${id}-cluster-count`);
      if (map.getSource(id)) map.removeSource(id);
    };
  }, [mapReady, id]);

  // Update data when POIs change
  useEffect(() => {
    if (!mapReady || !map || !markersReady) return;

    const source = map.getSource(id);
    if (!source) return;

    const features = Object.values(pois)
      .filter((poi) => {
        // Basic visibility check
        if (poi.visible === false) return false;
        if (poi.latitude == null || poi.longitude == null) return false;
        if (typeof poi.latitude !== 'number' || typeof poi.longitude !== 'number') return false;

        // Group filter check
        const groupKey = poi.groupId && poi.groupId > 0 ? poi.groupId : 'ungrouped';
        if (groupFilters[groupKey] === false) return false;

        return true;
      })
      .map((poi) => {
        const color = poi.color || '#3B82F6';
        const iconCode = poi.iconCode || null;
        const iconType = poi.iconType || null;
        const iconId = poi.iconId || null;

        // Determine marker ID (mesma lógica do carregamento)
        let markerId;
        if (iconType === 'lucide' && iconCode) {
          markerId = `poi-marker-lucide-${iconCode}-${color.replace('#', '')}`;
        } else if (iconType === 'png' && iconId && iconsById[iconId]?.path) {
          markerId = `poi-marker-png-${iconId}-${color.replace('#', '')}`;
        } else if (iconId && iconsById[iconId]?.path) {
          // Fallback para ícones legados (sem iconType definido)
          markerId = `poi-marker-png-${iconId}-${color.replace('#', '')}`;
        } else {
          markerId = `poi-marker-color-${color.replace('#', '')}`;
        }

        // Fallback to default if marker not loaded
        if (!loadedMarkers.has(markerId)) {
          markerId = 'poi-marker-default';
        }

        return {
          type: 'Feature',
          geometry: {
            type: 'Point',
            coordinates: [poi.longitude, poi.latitude],
          },
          properties: {
            id: poi.id,
            name: poi.name,
            description: poi.description || '',
            color,
            marker: markerId,
            address: poi.address || '',
            city: poi.city || '',
            state: poi.state || '',
            postalCode: poi.postalCode || '',
            contactName: poi.contactName || '',
            contactPhone: poi.contactPhone || '',
            contactEmail: poi.contactEmail || '',
            favorite: poi.favorite ? 'true' : 'false',
          },
        };
      });

    source.setData({
      type: 'FeatureCollection',
      features,
    });
  }, [mapReady, pois, id, markersReady, loadedMarkers, iconsById, groupFilters]);

  // Toggle visibility
  useEffect(() => {
    if (!mapReady || !map) return;
    try {
      const visibility = showPoi ? 'visible' : 'none';
      const layers = [`${id}-icons`, `${id}-labels`, `${id}-clusters`, `${id}-cluster-count`];
      layers.forEach((layerId) => {
        if (map.getLayer(layerId)) {
          map.setLayoutProperty(layerId, 'visibility', visibility);
        }
      });
    } catch {
      // Layers not yet created
    }
  }, [mapReady, showPoi, id]);

  return null;
};

MapPoi.handlesMapReady = true;

export default MapPoi;
